/**
 * Everything waiting on the parent, in one place.
 *
 * These are exactly the invoices that used to carry a "Pay Now" button down in the
 * enrollment list — the same set, under the same conditions. What changes is that they no
 * longer have to be found: a family with five enrollments had five rows to scan for a blue
 * button, and the two that needed paying looked like the three that did not.
 *
 * Collecting them here also lets the total be stated once. "$1,860 due Sep 1" is the sentence
 * a parent is actually trying to assemble, and it was previously left to them to add up.
 */

import { useNavigate } from 'react-router-dom';
import { TYPE_ATTRIBUTE, TYPE_CLOCK, TYPE_OBJECT, TYPE_SECTION_WIDE } from '../typeScale';
import { useLabelParts, useLabels } from '../../config/useLabels';
import { formatDateDisplay } from '../../utils/date';

/**
 * Something waiting on the parent that is not money.
 *
 * These used to hang off each child's row in the enrollment list, which the week calendar
 * replaced. A calendar has nowhere sensible to put "waiver not signed", and dropping the nudge
 * would have quietly removed the only place a parent is told about it — so it comes here, which
 * is the block that already means "things waiting on you".
 */
export interface DueAction {
  id: string;
  /** Who it concerns. */
  title: string;
  detail: string;
  cta: string;
  onAction: () => void;
  /**
   * Nice to have, rather than owed. A waiver has to be signed before a child can attend and a
   * conference slot disappears when the round closes; nobody is blocked by not inviting a
   * co-parent. Optional items sink below the money and go quiet, so that a block titled
   * "Needs you" keeps meaning it — five rows that all look equally urgent teach a parent to
   * skim past the two that are.
   */
  optional?: boolean;
  /**
   * The word on an optional row's pill, when "Optional" is not the truest thing to say — a
   * booked conference is not optional, it is done, and the row is there to change it.
   */
  optionalLabel?: string;
}

export interface DueInvoice {
  invoiceId: string;
  studentFirstName: string;
  programName: string;
  amountDue: number;
  /** The invoice's `due_date` — a DATE column, so formatted without timezone conversion. */
  dueDate: string;
}

/** `$1,860` — whole dollars, because a cents column on a total nobody edits is just noise. */
function formatDollars(amount: number): string {
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

export function NeedsYouSection({
  invoices,
  actions = [],
  className = '',
}: {
  invoices: DueInvoice[];
  actions?: DueAction[];
  className?: string;
}) {
  const navigate = useNavigate();
  const label = useLabelParts();
  const t = useLabels();

  if (invoices.length === 0 && actions.length === 0) return null;

  const heading = label('header.needsYou');
  const requiredActions = actions.filter(action => !action.optional);
  const optionalActions = actions.filter(action => action.optional);
  const total = invoices.reduce((sum, inv) => sum + inv.amountDue, 0);
  // The soonest deadline is the one that matters; a later one is not news yet.
  const soonest = invoices.length > 0
    ? invoices.reduce((a, b) => (a.dueDate <= b.dueDate ? a : b)).dueDate
    : null;

  // Required and optional rows share a shape so the block still reads as one list; what
  // separates them is weight. An optional row is greyed and labelled in words as well as in
  // colour, since colour alone is not something every parent can see.
  const renderAction = (action: DueAction) => (
    <div key={action.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className={`${TYPE_OBJECT} truncate ${action.optional ? 'text-gray-500' : ''}`}>{action.title}</p>
        <p className={TYPE_ATTRIBUTE}>
          {action.detail}
          {action.optional && (
            <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
              {action.optionalLabel ?? 'Optional'}
            </span>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={action.onAction}
        className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 ${
          action.optional
            ? 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 focus:ring-gray-300'
            : 'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-400'
        }`}
      >
        {action.cta}
      </button>
    </div>
  );

  return (
    <section className={`rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 lg:p-6 ${className}`} aria-label={heading.en}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className={TYPE_SECTION_WIDE}>
          {heading.zh && <span className="mr-1.5 font-normal normal-case tracking-normal">{heading.zh}</span>}
          {heading.en}
        </h2>
        {soonest && (
          <span className="text-xs font-semibold tabular-nums text-amber-700">
            {formatDollars(total)} due {formatDateDisplay(soonest)}
          </span>
        )}
      </div>

      <div className="divide-y divide-gray-100">
        {requiredActions.map(action => renderAction(action))}

        {invoices.map(invoice => (
          <div key={invoice.invoiceId} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
            <div className="min-w-0">
              <p className={`${TYPE_OBJECT} truncate`}>
                {invoice.studentFirstName} · {invoice.programName}
              </p>
              <p className={TYPE_ATTRIBUTE}>Due {formatDateDisplay(invoice.dueDate)}</p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2.5">
              <span className={`${TYPE_CLOCK} font-semibold text-gray-900`}>
                {formatDollars(invoice.amountDue)}
              </span>
              <button
                type="button"
                onClick={() => navigate(`/payment-checkout/${invoice.invoiceId}`)}
                className="brand-cta rounded-full px-3 py-1 text-xs font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {t('action.payNow')}
              </button>
            </div>
          </div>
        ))}

        {optionalActions.map(action => renderAction(action))}
      </div>
    </section>
  );
}
